'use client';

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';

interface Trade {
  id: string;
  timestamp: number;
  symbol: string;
  side: 'BUY' | 'SELL' | 'LONG' | 'SHORT';
  quantity: number;
  price: number;
  total: number;
  pnl?: number;
  pnlPercent?: number;
  model?: string;
  holdingTime?: number;
  entryPrice?: number;
  action?: 'OPEN' | 'CLOSE';
}

export function CompletedTrades() {
  const { t } = useLanguage();
  const [trades, setTrades] = useState<Trade[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedModel, setSelectedModel] = useState<string>('ALL');

  useEffect(() => {
    const fetchTrades = async () => {
      try {
        const response = await fetch('/api/bot');
        const data = await response.json();

        const allTrades: Trade[] = [];

        if (data.deepseek?.status?.accountState?.trades) {
          const deepseekTrades = data.deepseek.status.accountState.trades
            .filter((trade: any) => trade.action === 'CLOSE')
            .map((trade: any) => ({
              ...trade,
              model: 'DeepSeek-V3'
            }));
          allTrades.push(...deepseekTrades);
        }

        if (data.doubao?.status?.accountState?.trades) {
          const doubaoTrades = data.doubao.status.accountState.trades
            .filter((trade: any) => trade.action === 'CLOSE')
            .map((trade: any) => ({
              ...trade,
              model: 'Doubao'
            }));
          allTrades.push(...doubaoTrades);
        }

        if (data.qwen?.status?.accountState?.trades) {
          const qwenTrades = data.qwen.status.accountState.trades
            .filter((trade: any) => trade.action === 'CLOSE')
            .map((trade: any) => ({
              ...trade,
              model: 'Qwen'
            }));
          allTrades.push(...qwenTrades);
        }

        allTrades.sort((a, b) => b.timestamp - a.timestamp);

        setTrades(allTrades);
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch trades:', error);
        setLoading(false);
      }
    };

    fetchTrades();
    const interval = setInterval(fetchTrades, 3000);

    return () => clearInterval(interval);
  }, []);

  const filteredTrades = selectedModel === 'ALL'
    ? trades
    : trades.filter(t => t.model === selectedModel);

  const allModels = ['ALL', ...Array.from(new Set(trades.map(t => t.model).filter(Boolean)))];

  const formatHoldingTime = (ms: number) => {
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}H ${minutes}M`;
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-xs text-gray-500">{t.common.loadingTrades}</div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-black">
      {/* Filter Header */}
      <div className="border-b border-white/10 p-4">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-gray-400">
            {t.common.filter}:
          </span>
          <select
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            className="border border-white/20 bg-black px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-white focus:border-white focus:outline-none"
          >
            {allModels.map(model => (
              <option key={model} value={model}>
                {model === 'ALL' ? t.common.allModels : model}
              </option>
            ))}
          </select>
        </div>
        <div className="mt-2 text-right font-mono text-[10px] text-gray-500">
          {t.common.showingLast} {filteredTrades.length} {t.common.tradesText}
        </div>
      </div>

      {/* Trades List */}
      <div className="flex-1 overflow-y-auto">
        {filteredTrades.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-gray-500">
            <div className="font-mono text-xs">{t.trades.noTrades}</div>
          </div>
        ) : (
          <div className="divide-y divide-white/10">
            {filteredTrades.map((trade, index) => {
              const isLong = trade.side === 'BUY' || trade.side === 'LONG';
              const sideColor = isLong ? 'text-blue-400' : 'text-orange-400';
              const sideIcon = isLong ? '⬆' : '⬇';

              return (
                <motion.div
                  key={trade.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className="p-4 hover:bg-white/5"
                >
                  {/* Header Line */}
                  <div className="mb-2 flex items-start justify-between">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-mono text-xs`}>{sideIcon}</span>
                      <span className="font-mono text-xs font-semibold text-gray-300">
                        {trade.model}
                      </span>
                      <span className="font-mono text-xs text-gray-500">{t.common.completedA}</span>
                      <span className={`font-mono text-xs font-semibold ${sideColor}`}>
                        {isLong ? t.common.long.toLowerCase() : t.common.short.toLowerCase()}
                      </span>
                      <span className="font-mono text-xs text-gray-500">{t.common.tradeOn}</span>
                      <span className="font-mono text-xs font-semibold text-yellow-400">
                        ● {trade.symbol}!
                      </span>
                    </div>
                    <div className="font-mono text-[10px] text-gray-500 whitespace-nowrap">
                      {new Date(trade.timestamp).toLocaleString('en-US', {
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                      })}
                    </div>
                  </div>

                  {/* Details */}
                  <div className="ml-6 space-y-1 font-mono text-[11px] text-gray-400">
                    <div>
                      {t.common.price}: ${trade.entryPrice?.toFixed(2) || '0.00'} → ${trade.price.toFixed(2)}
                    </div>
                    <div>
                      {t.common.quantity}: {trade.quantity.toFixed(4)}
                    </div>
                    <div>
                      {t.common.notional}: ${((trade.entryPrice || 0) * trade.quantity).toFixed(2)} → ${trade.total.toFixed(2)}
                    </div>
                    {trade.holdingTime && (
                      <div>
                        {t.common.holdingTime}: {formatHoldingTime(trade.holdingTime)}
                      </div>
                    )}
                  </div>

                  {/* PnL */}
                  <div className="ml-6 mt-2 font-mono text-sm font-bold">
                    <span className={trade.pnl && trade.pnl >= 0 ? 'text-green-400' : 'text-red-400'}>
                      {t.common.netPnl}: {trade.pnl && trade.pnl >= 0 ? '+' : ''}${trade.pnl?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
